import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Typography,
  Alert,
  Button,
  Paper,
} from '@mui/material';
import { ArrowBack as BackIcon, Home as HomeIcon } from '@mui/icons-material';
import { courseApi, lessonApi } from '../services/api';

type DeepLinkType = 'course' | 'lesson';

interface DeepLinkState {
  loading: boolean;
  error: string | null;
  enrolling: boolean;
}

export default function DeepLinkPage() {
  const { type, slug } = useParams<{ type: string; slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine the type from the URL path
  const determineLinkType = (): DeepLinkType | null => {
    const path = location.pathname;
    if (path.includes('/learn/courses/')) return 'course';
    if (path.includes('/learn/lessons/')) return 'lesson';
    // Fallback: normalize type param
    if (type === 'courses' || type === 'course') return 'course';
    if (type === 'lessons' || type === 'lesson') return 'lesson';
    return null;
  };

  const [state, setState] = useState<DeepLinkState>({
    loading: true,
    error: null,
    enrolling: false,
  });

  useEffect(() => {
    const handleDeepLink = async () => {
      if (!slug) {
        setState({ loading: false, error: 'Kein Inhalt angegeben', enrolling: false });
        return;
      }

      const linkType = determineLinkType();

      try {
        if (linkType === 'lesson') {
          // Look up lesson to find its course
          const response = await lessonApi.getBySlug(slug);
          const lesson = response.data;

          // Navigate to course with lesson query param to auto-open it
          navigate(`/courses/${lesson.course.slug}?lesson=${slug}`, { replace: true });
        } else if (linkType === 'course') {
          // Check if course exists and user is enrolled
          try {
            const response = await courseApi.getCourse(slug);
            const course = response.data;

            // Check enrollment status
            const isEnrolled =
              course.enrollment?.status === 'enrolled' ||
              course.enrollment?.status === 'in_progress' ||
              course.enrollment?.status === 'completed';

            if (!isEnrolled) {
              // Auto-enroll the user
              setState((prev) => ({ ...prev, enrolling: true }));
              await courseApi.enrollCourse(slug);
            }

            // Navigate to course page
            navigate(`/courses/${slug}`, { replace: true });
          } catch (error: any) {
            if (error.response?.status === 404) {
              setState({
                loading: false,
                error: `Modul "${slug}" wurde nicht gefunden.`,
                enrolling: false,
              });
            } else {
              throw error;
            }
          }
        } else {
          // Unknown type, try course first
          try {
            await courseApi.getCourse(slug);
            navigate(`/courses/${slug}`, { replace: true });
          } catch {
            // Try lesson
            try {
              const response = await lessonApi.getBySlug(slug);
              navigate(`/courses/${response.data.course.slug}?lesson=${slug}`, { replace: true });
            } catch {
              setState({
                loading: false,
                error: `Inhalt "${slug}" wurde nicht gefunden.`,
                enrolling: false,
              });
            }
          }
        }
      } catch (error: any) {
        console.error('Deep link error:', error);
        setState({
          loading: false,
          error: error.response?.data?.error || 'Ein Fehler ist aufgetreten.',
          enrolling: false,
        });
      }
    };

    handleDeepLink();
  }, [type, slug, navigate]);

  if (state.loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
        gap={2}
      >
        <CircularProgress size={48} />
        <Typography variant="h6" color="text.secondary">
          {state.enrolling ? 'Einschreibung wird durchgeführt...' : 'Inhalt wird geladen...'}
        </Typography>
      </Box>
    );
  }

  if (state.error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <Paper sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            {state.error}
          </Alert>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Der angeforderte Link konnte nicht verarbeitet werden.
          </Typography>
          <Box display="flex" gap={2} justifyContent="center">
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={() => navigate(-1)}
            >
              Zurück
            </Button>
            <Button
              variant="contained"
              startIcon={<HomeIcon />}
              onClick={() => navigate('/courses')}
            >
              Zu den Modulen
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  // Fallback - should not reach here normally
  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
      <CircularProgress />
    </Box>
  );
}
